import { components } from 'api/goodtrust/swagger'
import { deleteUserFutureMessage, useUserFutureMessage } from 'api/goodtrust/user/futureMessage'
import { Avatar } from 'components/avatar/Avatar'
import { Button } from 'components/button/Button'
import {
  SFutureMessageCard,
  SFutureMessageContent,
  SFutureMessageImageArea,
  SFutureMessageLockedWrapper,
  SFutureMessageParagraph,
  SFutureMessageShareWrapper,
  SFutureMessageSmallText,
  SFutureMessageStack,
  SFutureMessageStatus,
  SFutureMessageTitle,
  SFutureMessageWrapper,
} from './FutureMessageCard.styled'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { BlockSpinner } from 'components/spinner/Spinner'
import { HStack, Stack } from 'components/Stack'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { Text } from 'components/Typography'
import { WizardGridCardShare } from 'components/wizard/wizardGridCard/WizardGridCard'
import { format, formatDistanceStrict, parseISO } from 'date-fns'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Router from 'next/router'
import { fireEvent, mutateAnalyticsData } from 'utils/analytics'
import { colors } from 'utils/colors'
import { formatName } from 'utils/format'
import { ImageWithFallback } from 'components/content/memory/imageWithFallback/ImageWithFallback'

export function FutureMessageCard(props: {
  futureMessage: components['schemas']['FutureMessageResponse'] | undefined
  onDataChange?: () => void
  openInNewTab?: boolean
}) {
  const { t } = useTranslation('life_stories')
  const { futureMessage } = props

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('life_stories.inside_page.future_messages.card_delete_modal_title')}
        danger
        onConfirm={async () => {
          try {
            const res = await deleteUserFutureMessage(futureMessage?.uuid)
            if (!res.ok) throw Error(res?.error?.toString?.())
            await props.onDataChange?.()
            toastSuccess('future_message_deleted')
            close()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_future_message')
          }
        }}
        close={close}
      />
    ) : null
  )

  let fmLink = ''
  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
  } = usePopperModal(
    () => (
      <>
        <Button
          type="button"
          variant="text"
          onClick={() => void Router.push(fmLink)}
          leftIcon={<Icon name="DocumentEdit" />}
        >
          {t('life_stories.inside_page.future_messages.card_delete_modal_edit_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          tone="danger"
          onClick={showDeleteModal}
          leftIcon={<Icon name="Bin" />}
        >
          {t('life_stories.inside_page.future_messages.card_delete_modal_delete_button')}
        </Button>
      </>
    ),
    { placement: 'bottom-end' }
  )

  if (!futureMessage) {
    return null
  } else {
    const isSent = !!futureMessage.sent_date
    const isFinished = futureMessage.state === 'FINISHED'
    fmLink = isSent
      ? `/me/future-message/${futureMessage.uuid}/public`
      : `/me/future-message/${futureMessage.uuid}`

    let statusIcon = null
    let statusText = null

    const deliveryDateParsed = futureMessage.delivery_date
      ? parseISO(futureMessage.delivery_date)
      : undefined
    if (!isSent) {
      if (isFinished) {
        if (
          !futureMessage.validation_errors &&
          deliveryDateParsed &&
          deliveryDateParsed.getTime() > Date.now()
        ) {
          statusIcon = <Icon name="Check" color={colors.green[200]} size="1rem" />

          statusText = t('life_stories.inside_page.future_messages.card_status_scheduled', {
            date: formatDistanceStrict(deliveryDateParsed, new Date()),
          })
        } else if (
          !futureMessage.validation_errors &&
          futureMessage.send_type === 'DEATH_CONFIRMED' &&
          futureMessage.days_after_death
        ) {
          statusIcon = <Icon name="Check" color={colors.green[200]} size="1rem" />
          statusText = t('life_stories.inside_page.future_messages.card_status_after_death')
        } else {
          statusIcon = <Icon name="Warning" color={colors.red[500]} size="1rem" />
          statusText = t('life_stories.inside_page.future_messages.card_status_action_needed')
        }
      } else {
        statusText = t('life_stories.inside_page.future_messages.card_status_action_needed', {
          date: format(
            futureMessage.created ? parseISO(futureMessage.created) : new Date(),
            "do 'of' MMMM yyyy"
          ),
        })
      }
    }

    const msgParagraph = futureMessage.preview_body?.substring(0, 127)

    const timeTraveled = futureMessage.created
      ? formatDistanceStrict(parseISO(futureMessage.created), new Date())
      : null

    const locked = futureMessage.locked
    const thumbnail = futureMessage?.thumbnail_url
    const showImage = !isFinished || thumbnail
    const fromAvatarImage = futureMessage.shared_from_avatar?.url

    return (
      <SFutureMessageCard ref={setReferenceElement}>
        {deleteModal}
        {menu}

        <SFutureMessageWrapper>
          <SFutureMessageContent
            type="button"
            variant="secondary"
            data-testid={`future-message-item-${futureMessage.uuid}`}
            disabled={locked}
            onClick={() => (props.openInNewTab ? window.open(fmLink) : Router.push(fmLink))}
          >
            {showImage ? (
              <SFutureMessageImageArea>
                {thumbnail ? (
                  <>
                    <ImageWithFallback
                      fallbackSrc={fromAvatarImage ?? ''}
                      src={thumbnail}
                      alt={futureMessage.shared_from_avatar?.name ?? 'thumbnail'}
                      fill
                    />
                  </>
                ) : (
                  <Icon name="ColorGoodTrust" size="3rem" />
                )}
              </SFutureMessageImageArea>
            ) : (
              <div css={{ height: '0.5rem' }} />
            )}

            <SFutureMessageStack space="0.75rem">
              {isSent && (
                <HStack space="0.5rem">
                  <Avatar
                    customName={
                      formatName(
                        futureMessage?.author_first_name,
                        futureMessage?.author_last_name
                      ) || futureMessage?.author_email
                    }
                    customUrl={futureMessage?.author_avatar?.url}
                    size={42}
                  />
                  <Stack>
                    <SFutureMessageSmallText css={{ fontWeight: 'bold' }}>
                      {futureMessage?.myself
                        ? t('life_stories.inside_page.future_messages.card_message_by_you')
                        : t('life_stories.inside_page.future_messages.card_message_by_you', {
                            who:
                              formatName(
                                futureMessage?.author_first_name,
                                futureMessage?.author_last_name
                              ) || futureMessage?.author_email,
                          })}
                    </SFutureMessageSmallText>
                    <SFutureMessageSmallText>
                      {t('life_stories.inside_page.future_messages.time_difference_text', {
                        duration: timeTraveled,
                      })}
                    </SFutureMessageSmallText>
                  </Stack>
                </HStack>
              )}

              <SFutureMessageTitle>
                {futureMessage?.title ??
                  t('life_stories.inside_page.future_messages.draft_card_title')}
              </SFutureMessageTitle>

              {!showImage && msgParagraph && (
                <SFutureMessageParagraph>{msgParagraph}</SFutureMessageParagraph>
              )}

              {!isSent && (
                <SFutureMessageStatus>
                  <HStack space="0.5rem">
                    {statusIcon}
                    <SFutureMessageSmallText>{statusText}</SFutureMessageSmallText>
                  </HStack>
                </SFutureMessageStatus>
              )}

              {isSent ? (
                <Button type="button" variant="round" css={{ marginTop: 'auto' }}>
                  {t('life_stories.inside_page.future_messages.read_message_button')}
                </Button>
              ) : (
                <SFutureMessageShareWrapper>
                  <WizardGridCardShare
                    contacts={futureMessage?.recipients?.map((r) => ({
                      email: r,
                    }))}
                  />
                  <span />
                  <Button
                    type="button"
                    variant="text"
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      openMenu()
                    }}
                  >
                    <Icon name="MoreInfo" size="1rem" />
                  </Button>
                </SFutureMessageShareWrapper>
              )}
            </SFutureMessageStack>
          </SFutureMessageContent>

          {locked && (
            <Link href={`/subscription/future-messages`} legacyBehavior>
              <SFutureMessageLockedWrapper
                onClick={() => {
                  mutateAnalyticsData({ flow: 'future_message' })
                  fireEvent('fm_paywall_hit', { create: 'no' })
                }}
              >
                <HStack>
                  <Icon name="LockClosed" color={colors.blue[500]} />
                  <Text css={{ color: 'var(--c-blue500)', fontWeight: 'bold' }}>
                    {t('life_stories.inside_page.future_messages.unlock_content_button')}
                  </Text>
                </HStack>
              </SFutureMessageLockedWrapper>
            </Link>
          )}
        </SFutureMessageWrapper>
      </SFutureMessageCard>
    )
  }
}

export function FutureMessageCardByUuid(props: { uuid?: string | null; openInNewTab?: boolean }) {
  const futureSwr = useUserFutureMessage(props.uuid)

  if (!futureSwr.data && futureSwr.isValidating) {
    return <BlockSpinner />
  } else {
    return (
      <FutureMessageCard futureMessage={futureSwr?.data?.json} openInNewTab={props.openInNewTab} />
    )
  }
}
